import React, { useMemo } from 'react';
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { variablePrivacyDecisionTestIds } from '@test-kit/selectors/variable-privacy';

export interface AnalyticsPrivacyDecisionFieldProps {
  value: boolean | undefined;
  onChange: (value: boolean) => void;
  testIdPrefix: string;
  disabled?: boolean;
}

/**
 * Explicit two-choice editor over a tri-state value.
 *
 * `undefined` is intentionally rendered as "review required". It is never
 * coerced to `false`; the user must choose collect or exclude explicitly.
 */
export const AnalyticsPrivacyDecisionField: React.FC<AnalyticsPrivacyDecisionFieldProps> = ({
  value,
  onChange,
  testIdPrefix,
  disabled = false,
}) => {
  const testIds = useMemo(
    () => variablePrivacyDecisionTestIds(testIdPrefix),
    [testIdPrefix],
  );
  const selected = value === true ? 'exclude' : value === false ? 'collect' : '';

  return (
    <FormControl error={value === undefined} disabled={disabled}>
      <FormLabel id={`${testIds.group}-label`}>Analytics collection</FormLabel>
      <RadioGroup
        aria-labelledby={`${testIds.group}-label`}
        data-testid={testIds.group}
        value={selected}
        onChange={(event) => onChange(event.target.value === 'exclude')}
      >
        <FormControlLabel
          value="collect"
          control={(
            <Radio
              inputProps={{
                'data-testid': testIds.collect,
              } as unknown as React.InputHTMLAttributes<HTMLInputElement>}
            />
          )}
          label="Collect this variable in analytics"
        />
        <FormControlLabel
          value="exclude"
          control={(
            <Radio
              inputProps={{
                'data-testid': testIds.exclude,
              } as unknown as React.InputHTMLAttributes<HTMLInputElement>}
            />
          )}
          label="Do not collect this variable in analytics"
        />
      </RadioGroup>
      {value === undefined ? (
        <Alert severity="warning" data-testid={testIds.reviewRequired} sx={{ mt: 1, py: 0 }}>
          <Typography variant="caption">
            Review required. Analytics-enabled projects cannot be published until every variable has an explicit decision.
          </Typography>
        </Alert>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Routing and personalization continue to work even when analytics collection is disabled.
        </Typography>
      )}
    </FormControl>
  );
};
